<template>
  <div class="chat">
    <v-toolbar color="indigo" density="compact" class="chat-toolbar">
      <v-icon class="ml-3">mdi-chat-processing-outline</v-icon>
      <v-toolbar-title class="text-subtitle-1">{{ title }}</v-toolbar-title>
      <v-btn icon size="small" :title="refreshTitle" :disabled="chat.streaming" @click="chat.refreshData()">
        <v-icon :color="chat.refreshNext ? 'amber' : undefined">mdi-database-refresh-outline</v-icon>
      </v-btn>
      <v-btn icon size="small" :title="t('aiChat.newChat')" @click="chat.newChat()">
        <v-icon>mdi-plus-circle-outline</v-icon>
      </v-btn>
      <v-btn icon size="small" :title="t('aiChat.close')" @click="chat.open = false">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-toolbar>

    <div class="chat-head px-3 pt-3">
      <v-select
        v-model="chat.model"
        :items="chat.models"
        :label="t('aiChat.model')"
        :disabled="chat.streaming"
        density="compact"
        variant="outlined"
        hide-details
      />
      <v-alert v-if="keyMissing" type="warning" density="compact" variant="tonal" class="mt-2">
        {{ t('aiChat.keyMissing', { model: chat.model }) }}
      </v-alert>
      <div v-if="chat.refreshNext" class="text-caption text-amber-darken-3 mt-1">
        {{ refreshQueued }}
      </div>
      <div v-if="project && chat.documentCount" class="text-caption text-medium-emphasis mt-1">
        {{ t('aiChat.project.documents', { n: chat.documentCount }) }}
      </div>
    </div>

    <div ref="scroller" class="chat-body px-3 py-2">
      <div v-if="!chat.messages.length" class="py-4">
        <div class="text-subtitle-2 mb-1">{{ emptyTitle }}</div>
        <div class="text-caption text-medium-emphasis mb-3">{{ emptyHint }}</div>
        <v-chip
          v-for="key in suggestionKeys"
          :key="key"
          class="ma-1 suggestion"
          size="small"
          variant="outlined"
          color="indigo"
          @click="chat.send(t(key))"
        >
          {{ t(key) }}
        </v-chip>
      </div>

      <div v-for="m in chat.messages" :key="m.id" :class="['msg', m.role]">
        <div v-if="m.role === 'user'" class="bubble user-bubble">{{ m.text }}</div>

        <div v-else class="bubble assistant-bubble">
          <v-expansion-panels v-if="m.activity.length || m.thinking" flat class="mb-1 activity">
            <v-expansion-panel>
              <v-expansion-panel-title class="text-caption activity-title">
                <v-progress-circular v-if="m.streaming" indeterminate size="12" width="2" color="indigo" class="mr-2" />
                <v-icon v-else size="small" class="mr-2">mdi-cog-outline</v-icon>
                {{ t('aiChat.activity', { n: m.activity.filter(a => a.kind === 'phase').length }) }}
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <div
                  v-for="(a, i) in m.activity"
                  :key="i"
                  :class="['text-caption', { 'text-medium-emphasis': a.kind === 'trace' }]"
                >
                  {{ a.text }}
                  <pre v-if="a.detail" class="detail">{{ a.detail }}</pre>
                </div>
                <details v-if="m.thinking" class="mt-1">
                  <summary class="text-caption">{{ t('aiChat.thinking') }}</summary>
                  <pre class="detail">{{ m.thinking }}</pre>
                </details>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>

          <div v-if="m.text" class="markdown" v-html="render(m.text)" />
          <div v-else-if="m.streaming" class="text-caption text-medium-emphasis">
            {{ project && chat.preparing ? t('aiChat.project.readingFiles') : t('aiChat.working') }}
          </div>

          <v-alert v-if="m.error" type="error" density="compact" variant="tonal" class="mt-1">{{ m.error }}</v-alert>
          <div v-if="m.costUsd != null" class="text-caption text-medium-emphasis text-right mt-1">
            US$ {{ m.costUsd.toFixed(4) }}
          </div>
        </div>
      </div>
    </div>

    <div class="chat-input pa-2">
      <v-textarea
        v-model="draft"
        :placeholder="t('aiChat.placeholder')"
        rows="1"
        max-rows="6"
        auto-grow
        density="compact"
        variant="outlined"
        hide-details
        @keydown.enter.exact="onEnter"
      />
      <div class="d-flex align-center mt-1">
        <span class="text-caption text-medium-emphasis">
          {{ t('aiChat.totalCost', { cost: chat.totalCostUsd.toFixed(4) }) }}
        </span>
        <v-spacer />
        <v-btn v-if="chat.streaming" size="small" color="red" variant="tonal" prepend-icon="mdi-stop" @click="chat.stop()">
          {{ t('aiChat.stop') }}
        </v-btn>
        <v-btn
          v-else
          size="small"
          color="indigo"
          variant="flat"
          prepend-icon="mdi-send"
          :disabled="!draft.trim() || !chat.model"
          @click="submit"
        >
          {{ t('aiChat.send') }}
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { marked } from 'marked'
import DOMPurify from 'dompurify'

// `chat` è lo store della conversazione (chat admin o chat di processo): stessa interfaccia.
const props = defineProps({
  chat: { type: Object, required: true },
  project: { type: Boolean, default: false }
})

const { t } = useI18n()
const chat = computed(() => props.chat)
const draft = ref('')
const scroller = ref(null)

const prefix = computed(() => (props.project ? 'aiChat.project.' : 'aiChat.'))
const title = computed(() => t(props.project ? 'aiChat.project.title' : 'aiChat.title'))
const emptyTitle = computed(() => t(`${prefix.value}emptyTitle`))
const emptyHint = computed(() => t(`${prefix.value}emptyHint`))
const refreshTitle = computed(() => t(props.project ? 'aiChat.project.refreshData' : 'aiChat.refreshData'))
const refreshQueued = computed(() => t(props.project ? 'aiChat.project.refreshQueued' : 'aiChat.refreshQueued'))

const suggestionKeys = computed(() => props.project
  ? [
      'aiChat.project.suggestions.summary',
      'aiChat.project.suggestions.documents',
      'aiChat.project.suggestions.check',
      'aiChat.project.suggestions.pending'
    ]
  : [
      'aiChat.suggestions.overdue',
      'aiChat.suggestions.bottleneck',
      'aiChat.suggestions.stuck',
      'aiChat.suggestions.verifications'
    ])

// Conta solo la chiave DeepSeek: gli altri fornitori (es. Ollama locale) non ne hanno una.
const keyMissing = computed(() => {
  if (!chat.value.model) return false
  return chat.value.model.toLowerCase().startsWith('deepseek') && !chat.value.keys.deepSeek
})

// Il testo arriva dal modello: va sempre sanificato prima di finire in v-html.
const render = (text) => DOMPurify.sanitize(marked.parse(text, { gfm: true, breaks: true }))

const submit = () => {
  if (chat.value.streaming || !chat.value.model) return
  const text = draft.value
  if (!text.trim()) return
  draft.value = ''
  chat.value.send(text)
}

const onEnter = (event) => {
  // Durante la composizione (IME) Invio conferma il carattere, non il messaggio.
  if (event.isComposing) return
  event.preventDefault()
  submit()
}

// Segue la risposta mentre arriva, ma solo se chi legge è già in fondo: chi è risalito a rileggere
// non deve essere trascinato giù a ogni parola.
watch(
  () => {
    const last = chat.value.messages[chat.value.messages.length - 1]
    return [chat.value.messages.length, last?.text?.length, last?.activity?.length]
  },
  async ([count], [previousCount] = []) => {
    const el = scroller.value
    if (!el) return
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 120
    await nextTick()
    if (nearBottom || count !== previousCount) el.scrollTop = el.scrollHeight
  }
)
</script>

<style scoped>
.chat {
  display: flex;
  flex-direction: column;
  height: 100%;
  /* Senza questo la colonna cresce col contenuto e a scorrere è tutto il pannello, barra compresa. */
  min-height: 0;
  overflow: hidden;
}

/* La barra resta in cima: scorre solo l'elenco dei messaggi. */
.chat-toolbar {
  flex: 0 0 auto;
  position: sticky;
  top: 0;
  z-index: 2;
}

.chat-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}

.chat-head {
  flex: 0 0 auto;
}

.chat-input {
  flex: 0 0 auto;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}

.msg {
  display: flex;
  margin-bottom: 10px;
}

.msg.user {
  justify-content: flex-end;
}

.bubble {
  border-radius: 10px;
  padding: 8px 10px;
  font-size: 0.875rem;
  max-width: 92%;
}

.user-bubble {
  background: #e8eaf6;
  white-space: pre-wrap;
}

.assistant-bubble {
  background: #fafafa;
  border: 1px solid rgba(0, 0, 0, 0.08);
  width: 100%;
  max-width: 100%;
}

.suggestion {
  white-space: normal;
  height: auto !important;
  padding-block: 4px;
}

.activity :deep(.v-expansion-panel) {
  background: transparent;
}

.activity-title {
  min-height: 28px !important;
  padding: 2px 8px;
}

.detail {
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 0.7rem;
  max-height: 200px;
  overflow: auto;
  background: #f0f0f0;
  padding: 4px;
  margin: 2px 0 6px;
}

.markdown :deep(p) {
  margin: 0 0 6px;
}

.markdown :deep(ul),
.markdown :deep(ol) {
  padding-left: 18px;
  margin-bottom: 6px;
}

.markdown :deep(table) {
  display: block;
  overflow-x: auto;
  border-collapse: collapse;
  font-size: 0.8rem;
  margin-bottom: 6px;
}

.markdown :deep(th),
.markdown :deep(td) {
  border: 1px solid rgba(0, 0, 0, 0.15);
  padding: 2px 6px;
  text-align: left;
}

.markdown :deep(th) {
  background: #eeeeee;
}

.markdown :deep(code) {
  background: #eeeeee;
  padding: 0 3px;
  border-radius: 3px;
  font-size: 0.8rem;
}
</style>
